import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Calendar, Users, DollarSign, LogOut, TrendingUp, Sparkles, Lightbulb, UserCheck, ClipboardList, PieChart, Wand2, Bot, Shield, AlertCircle, RefreshCw, Inbox } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import api, { isApiConfigured, getConfiguredApiUrl } from '../utils/api';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [financialStats, setFinancialStats] = useState(null);
  const [rfps, setRFPs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    // Check if API is configured
    if (!isApiConfigured()) {
      setError({
        type: 'config',
        message: 'Backend URL not configured. Please set REACT_APP_BACKEND_URL.',
        details: `Current API URL: ${getConfiguredApiUrl()}`
      });
      setLoading(false);
      return;
    }
    
    try {
      const [statsRes, finStatsRes, rfpsRes, projectsRes, invoicesRes] = await Promise.all([
        api.getStats(),
        api.getFinancialDashboard(),
        api.getRFPs(),
        api.getProjects(),
        api.getInvoices(),
      ]);
      setStats(statsRes.data);
      setFinancialStats(finStatsRes.data);
      setRFPs(rfpsRes.data);
      setProjects(projectsRes.data);
      setInvoices(invoicesRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      
      // Categorize the error
      if (err.response?.status === 401) {
        setError({
          type: 'auth',
          message: 'Session expired or unauthorized. Please log in again.',
          details: 'Your authentication token may have expired.'
        });
      } else if (err.response?.status === 403) {
        setError({
          type: 'permission',
          message: 'You do not have permission to access this dashboard.',
          details: 'Admin role required.'
        });
      } else if (err.response?.status >= 500) {
        setError({
          type: 'server',
          message: 'Server error. Please try again later.',
          details: err.response?.data?.detail || 'Internal server error'
        });
      } else if (!err.response) {
        setError({
          type: 'network',
          message: 'Unable to connect to server.',
          details: err.friendlyMessage || 'Check your internet connection or CORS settings.'
        });
      } else {
        setError({
          type: 'unknown',
          message: 'Failed to load dashboard data.',
          details: err.message
        });
      }
      
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleRFPStatusUpdate = async (rfpId, status) => {
    try {
      await api.updateRFPStatus(rfpId, status, null, null);
      toast.success(`RFP ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update RFP');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      submitted: 'text-yellow-400',
      reviewing: 'text-blue-400',
      approved: 'text-green-400',
      rejected: 'text-red-400',
      converted_to_project: 'text-purple-400',
    };
    return colors[status] || 'text-white/60';
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0F] border-r border-white/5 p-6 z-50">
        <Link to="/" className="flex items-center space-x-2 mb-10">
          <div className="text-2xl font-bold tracking-tight">
            <span className="text-[#D4AF37]">NEHPETS</span>
            <span className="text-white">+</span>
          </div>
        </Link>

        <div className="space-y-2 mb-10">
          <Link
            to="/dashboard/admin"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-overview"
          >
            <LayoutDashboard size={20} />
            <span>Overview</span>
          </Link>
          <Link
            to="/admin/inquiries"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-inquiries"
          >
            <Inbox size={20} />
            <span>Public Inquiries</span>
          </Link>
          <Link
            to="/dashboard/admin/rfps"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-rfps"
          >
            <FileText size={20} />
            <span>RFPs</span>
          </Link>
          <Link
            to="/dashboard/admin/projects"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-projects"
          >
            <Calendar size={20} />
            <span>Projects</span>
          </Link>
          <Link
            to="/dashboard/admin/financial"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-financial"
          >
            <DollarSign size={20} />
            <span>Financial</span>
          </Link>
          <Link
            to="/dashboard/admin/ai-tools"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-ai-tools"
          >
            <Sparkles size={20} />
            <span>AI Tools</span>
          </Link>
          <Link
            to="/dashboard/admin/clients"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-clients"
          >
            <Users size={20} />
            <span>Clients</span>
          </Link>
          <Link
            to="/dashboard/admin/analytics"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-analytics"
          >
            <TrendingUp size={20} />
            <span>Analytics</span>
          </Link>
          <Link
            to="/dashboard/admin/enterprise"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
            data-testid="nav-enterprise"
          >
            <Shield size={20} />
            <span>Enterprise</span>
          </Link>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            className="w-full bg-transparent border border-white/20 text-white hover:bg-white hover:text-black text-xs py-3"
            data-testid="logout-button"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <Routes>
          <Route
            path="/"
            element={
              <div>
                {/* Header */}
                <div className="mb-8">
                  <h1 className="text-4xl font-medium mb-2">Admin Dashboard</h1>
                  <p className="text-white/60">Manage your events, clients, and financials</p>
                </div>

                {/* Stats Grid */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div>
                    <span className="ml-3 text-white/60">Loading dashboard...</span>
                  </div>
                ) : error ? (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center" data-testid="dashboard-error">
                    <AlertCircle className="mx-auto mb-4 text-red-400" size={48} />
                    <h3 className="text-xl font-medium text-red-400 mb-2">{error.message}</h3>
                    <p className="text-white/60 text-sm mb-4">{error.details}</p>
                    {error.type === 'auth' ? (
                      <Button 
                        onClick={() => { logout(); navigate('/login'); }}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Go to Login
                      </Button>
                    ) : (
                      <Button 
                        onClick={fetchData}
                        className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                      >
                        <RefreshCw size={16} className="mr-2" />
                        Retry
                      </Button>
                    )}
                    {error.type === 'config' && (
                      <p className="text-xs text-white/40 mt-4">
                        API URL: {getConfiguredApiUrl()}
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="text-3xl font-bold text-[#D4AF37] mb-2">{stats?.total_rfps || 0}</div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Total RFPs</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="text-3xl font-bold text-[#D4AF37] mb-2">{stats?.total_projects || 0}</div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Active Projects</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="text-3xl font-bold text-[#D4AF37] mb-2">{stats?.total_clients || 0}</div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Total Clients</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="text-3xl font-bold text-yellow-400 mb-2">{stats?.pending_rfps || 0}</div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Pending RFPs</div>
                      </div>
                    </div>

                    {/* Financial Stats */}
                    {financialStats && (
                      <div className="mb-8">
                        <div className="flex items-center mb-4">
                          <TrendingUp className="text-[#D4AF37] mr-2" size={24} />
                          <h2 className="text-2xl font-medium">Financial Overview</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 p-6 rounded">
                            <div className="text-sm text-[#D4AF37] uppercase tracking-wider mb-2">Total Revenue</div>
                            <div className="text-3xl font-bold text-white">
                              ₦{(financialStats.total_revenue || 0).toLocaleString()}
                            </div>
                          </div>
                          <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 p-6 rounded">
                            <div className="text-sm text-blue-400 uppercase tracking-wider mb-2">Outstanding</div>
                            <div className="text-3xl font-bold text-white">
                              ₦{(financialStats.outstanding_payments || 0).toLocaleString()}
                            </div>
                          </div>
                          <div className="bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20 p-6 rounded">
                            <div className="text-sm text-green-400 uppercase tracking-wider mb-2">Subscriptions</div>
                            <div className="text-3xl font-bold text-white">
                              {financialStats.active_subscriptions || 0}
                            </div>
                            <div className="text-xs text-white/60 mt-1">
                              ₦{(financialStats.subscription_revenue || 0).toLocaleString()}/mo
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pending RFPs */}
                    <div className="mb-8">
                      <h2 className="text-2xl font-medium mb-4">Pending RFPs</h2>
                      {rfps.filter(r => r.status === 'submitted').length === 0 ? (
                        <div className="bg-[#0A0A0F] border border-white/5 p-6 text-center text-white/60 rounded">
                          No pending RFPs
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {rfps.filter(r => r.status === 'submitted').map((rfp) => (
                            <div
                              key={rfp.rfp_id}
                              className="bg-[#0A0A0F] border border-white/5 p-6 rounded"
                              data-testid={`rfp-${rfp.rfp_id}`}
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div>
                                  <h3 className="text-xl font-medium mb-1">{rfp.client_name}</h3>
                                  <p className="text-white/60 text-sm">{rfp.client_email}</p>
                                </div>
                                <span className={`text-sm uppercase tracking-wider ${getStatusColor(rfp.status)}`}>
                                  {rfp.status}
                                </span>
                              </div>
                              <div className="mb-4">
                                <p className="text-white/80 mb-2"><strong>Event Type:</strong> {rfp.event_type}</p>
                                <p className="text-white/80 mb-2"><strong>Date:</strong> {rfp.event_date}</p>
                                <p className="text-white/80 mb-2"><strong>Budget:</strong> {rfp.budget_range}</p>
                                <p className="text-white/60 text-sm">{rfp.description}</p>
                              </div>
                              <div className="flex space-x-3">
                                <Button
                                  onClick={() => handleRFPStatusUpdate(rfp.rfp_id, 'reviewing')}
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-2"
                                  data-testid={`review-btn-${rfp.rfp_id}`}
                                >
                                  Mark as Reviewing
                                </Button>
                                <Button
                                  onClick={() => handleRFPStatusUpdate(rfp.rfp_id, 'approved')}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs px-4 py-2"
                                  data-testid={`approve-btn-${rfp.rfp_id}`}
                                >
                                  Approve
                                </Button>
                                <Button
                                  onClick={() => handleRFPStatusUpdate(rfp.rfp_id, 'rejected')}
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs px-4 py-2"
                                  data-testid={`reject-btn-${rfp.rfp_id}`}
                                >
                                  Reject
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Recent Projects */}
                    <div>
                      <h2 className="text-2xl font-medium mb-4">Recent Projects</h2>
                      {projects.length === 0 ? (
                        <div className="bg-[#0A0A0F] border border-white/5 p-6 text-center text-white/60 rounded">
                          No projects yet
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {projects.slice(0, 5).map((project) => (
                            <div
                              key={project.project_id}
                              className="bg-[#0A0A0F] border border-white/5 p-6 rounded"
                              data-testid={`project-${project.project_id}`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="text-lg font-medium mb-1">{project.project_name}</h3>
                                  <p className="text-white/60 text-sm">Client: {project.client_email}</p>
                                  <p className="text-white/60 text-sm">Phase: {project.phase}</p>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm text-white/60">{project.event_date || 'TBD'}</span>
                                  <p className="text-xs text-[#D4AF37] mt-1">
                                    {project.budget?.estimated ? `₦${project.budget.estimated.toLocaleString()}` : 'Budget TBD'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            }
          />
          <Route path="/rfps" element={<div className="text-white">All RFPs - Coming Soon</div>} />
          <Route path="/projects" element={<div className="text-white">All Projects - Coming Soon</div>} />
          <Route path="/financial" element={<FinancialDashboard invoices={invoices} />} />
          <Route path="/ai-tools" element={<AdminAITools projects={projects} rfps={rfps} />} />
          <Route path="/analytics" element={<AnalyticsDashboard />} />
          <Route path="/enterprise" element={<EnterpriseSection />} />
          <Route path="/clients" element={<div className="text-white">Client Management - Coming Soon</div>} />
        </Routes>
      </main>
    </div>
  );
};

// AI Tools Component for Admin Dashboard
const AdminAITools = ({ projects, rfps }) => {
  const [activeTab, setActiveTab] = useState('concept');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Concept Generation State
  const [conceptForm, setConceptForm] = useState({
    event_type: '',
    theme: '',
    budget_range: '',
    guest_count: '',
    special_requirements: ''
  });
  
  // Vendor Recommendation State
  const [vendorForm, setVendorForm] = useState({
    event_type: '',
    location: 'Lagos',
    budget_range: ''
  });
  
  // Program Flow State
  const [flowForm, setFlowForm] = useState({
    event_type: '',
    duration_hours: 4,
    activities: ''
  });

  // Budget Analysis State
  const [budgetForm, setBudgetForm] = useState({
    project_id: '',
    estimated_budget: '',
    guest_count: '',
    event_type: '',
    venue_type: ''
  });

  // Proposal Generation State
  const [selectedRfpId, setSelectedRfpId] = useState('');

  const handleGenerateConcept = async () => {
    if (!conceptForm.event_type) {
      toast.error('Please enter an event type');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.generateEventConcept({
        ...conceptForm,
        guest_count: conceptForm.guest_count ? parseInt(conceptForm.guest_count) : null
      });
      setResult({ type: 'concept', data: response.data });
      toast.success('Concept generated successfully!');
    } catch (error) {
      toast.error('Failed to generate concept');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendVendors = async () => {
    if (!vendorForm.event_type || !vendorForm.location) {
      toast.error('Please fill in event type and location');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.recommendVendors(
        vendorForm.event_type,
        vendorForm.location,
        vendorForm.budget_range
      );
      setResult({ type: 'vendors', data: response.data });
      toast.success('Vendor recommendations generated!');
    } catch (error) {
      toast.error('Failed to get vendor recommendations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeFlow = async () => {
    if (!flowForm.event_type || !flowForm.activities) {
      toast.error('Please fill in event type and activities');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const activitiesArray = flowForm.activities.split('\n').filter(a => a.trim());
      const response = await api.optimizeProgramFlow(
        flowForm.event_type,
        flowForm.duration_hours,
        activitiesArray
      );
      setResult({ type: 'flow', data: response.data });
      toast.success('Program flow optimized!');
    } catch (error) {
      toast.error('Failed to optimize program flow');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeBudget = async () => {
    if (!budgetForm.project_id || !budgetForm.estimated_budget) {
      toast.error('Please select a project and enter budget');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.analyzeBudget({
        ...budgetForm,
        estimated_budget: parseFloat(budgetForm.estimated_budget),
        guest_count: parseInt(budgetForm.guest_count) || 100
      });
      setResult({ type: 'budget', data: response.data });
      toast.success('Budget analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze budget');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProposal = async () => {
    if (!selectedRfpId) {
      toast.error('Please select an RFP');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.generateProposal(selectedRfpId);
      setResult({ type: 'proposal', data: response.data });
      toast.success('Proposal generated!');
    } catch (error) {
      toast.error('Failed to generate proposal');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'concept', label: 'Event Concept', icon: Lightbulb },
    { id: 'vendors', label: 'Vendor Finder', icon: UserCheck },
    { id: 'flow', label: 'Program Flow', icon: ClipboardList },
    { id: 'budget', label: 'Budget Analysis', icon: PieChart },
    { id: 'proposal', label: 'Proposal Writer', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Bot className="text-[#D4AF37] mr-3" size={28} />
        <div>
          <h2 className="text-2xl font-medium">AI-Powered Tools</h2>
          <p className="text-white/60 text-sm">Leverage AI to streamline your event planning</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setResult(null); }}
            className={`flex items-center px-4 py-2 rounded-lg text-sm transition-all ${
              activeTab === tab.id 
                ? 'bg-[#D4AF37] text-black font-medium' 
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            <tab.icon size={16} className="mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          {activeTab === 'concept' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <Sparkles size={20} className="mr-2" />
                Generate Event Concept
              </h3>
              <p className="text-white/60 text-sm">Create unique event concepts with AI assistance</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Event Type *</label>
                <input
                  type="text"
                  value={conceptForm.event_type}
                  onChange={(e) => setConceptForm({ ...conceptForm, event_type: e.target.value })}
                  placeholder="e.g., Corporate Gala, Wedding, Product Launch"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Theme (Optional)</label>
                <input
                  type="text"
                  value={conceptForm.theme}
                  onChange={(e) => setConceptForm({ ...conceptForm, theme: e.target.value })}
                  placeholder="e.g., Elegant Gold, Modern Minimalist"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/80 text-sm block mb-2">Budget Range</label>
                  <input
                    type="text"
                    value={conceptForm.budget_range}
                    onChange={(e) => setConceptForm({ ...conceptForm, budget_range: e.target.value })}
                    placeholder="e.g., ₦5M - ₦10M"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/80 text-sm block mb-2">Guest Count</label>
                  <input
                    type="number"
                    value={conceptForm.guest_count}
                    onChange={(e) => setConceptForm({ ...conceptForm, guest_count: e.target.value })}
                    placeholder="e.g., 200"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Special Requirements</label>
                <textarea
                  value={conceptForm.special_requirements}
                  onChange={(e) => setConceptForm({ ...conceptForm, special_requirements: e.target.value })}
                  placeholder="Any specific requirements or preferences..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <Button
                onClick={handleGenerateConcept}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Generating...' : 'Generate Concept'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'vendors' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <UserCheck size={20} className="mr-2" />
                Vendor Recommendations
              </h3>
              <p className="text-white/60 text-sm">Get AI-curated vendor suggestions for your event</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Event Type *</label>
                <input
                  type="text"
                  value={vendorForm.event_type}
                  onChange={(e) => setVendorForm({ ...vendorForm, event_type: e.target.value })}
                  placeholder="e.g., Wedding, Corporate Event"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Location *</label>
                <select
                  value={vendorForm.location}
                  onChange={(e) => setVendorForm({ ...vendorForm, location: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                >
                  <option value="Lagos">Lagos</option>
                  <option value="Abuja">Abuja</option>
                  <option value="Port Harcourt">Port Harcourt</option>
                  <option value="Ibadan">Ibadan</option>
                  <option value="Kano">Kano</option>
                </select>
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Budget Range</label>
                <input
                  type="text"
                  value={vendorForm.budget_range}
                  onChange={(e) => setVendorForm({ ...vendorForm, budget_range: e.target.value })}
                  placeholder="e.g., ₦2M - ₦5M"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <Button
                onClick={handleRecommendVendors}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Finding Vendors...' : 'Get Recommendations'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'flow' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <ClipboardList size={20} className="mr-2" />
                Program Flow Optimizer
              </h3>
              <p className="text-white/60 text-sm">Optimize your event timeline and activities</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Event Type *</label>
                <input
                  type="text"
                  value={flowForm.event_type}
                  onChange={(e) => setFlowForm({ ...flowForm, event_type: e.target.value })}
                  placeholder="e.g., Conference, Wedding Reception"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Duration (Hours)</label>
                <input
                  type="number"
                  value={flowForm.duration_hours}
                  onChange={(e) => setFlowForm({ ...flowForm, duration_hours: parseInt(e.target.value) })}
                  min="1"
                  max="24"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Activities (One per line) *</label>
                <textarea
                  value={flowForm.activities}
                  onChange={(e) => setFlowForm({ ...flowForm, activities: e.target.value })}
                  placeholder="Registration&#10;Keynote Speech&#10;Networking Break&#10;Panel Discussion&#10;Closing Ceremony"
                  rows={6}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <Button
                onClick={handleOptimizeFlow}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Optimizing...' : 'Optimize Flow'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'budget' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <PieChart size={20} className="mr-2" />
                Budget Risk Analysis
              </h3>
              <p className="text-white/60 text-sm">Analyze and optimize your event budget</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Select Project *</label>
                <select
                  value={budgetForm.project_id}
                  onChange={(e) => setBudgetForm({ ...budgetForm, project_id: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                >
                  <option value="">Select a project...</option>
                  {projects.map(p => (
                    <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/80 text-sm block mb-2">Estimated Budget (₦) *</label>
                  <input
                    type="number"
                    value={budgetForm.estimated_budget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, estimated_budget: e.target.value })}
                    placeholder="5000000"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/80 text-sm block mb-2">Guest Count</label>
                  <input
                    type="number"
                    value={budgetForm.guest_count}
                    onChange={(e) => setBudgetForm({ ...budgetForm, guest_count: e.target.value })}
                    placeholder="200"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/80 text-sm block mb-2">Event Type</label>
                  <input
                    type="text"
                    value={budgetForm.event_type}
                    onChange={(e) => setBudgetForm({ ...budgetForm, event_type: e.target.value })}
                    placeholder="Corporate Gala"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/80 text-sm block mb-2">Venue Type</label>
                  <input
                    type="text"
                    value={budgetForm.venue_type}
                    onChange={(e) => setBudgetForm({ ...budgetForm, venue_type: e.target.value })}
                    placeholder="Ballroom Hotel"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
              </div>
              <Button
                onClick={handleAnalyzeBudget}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Analyzing...' : 'Analyze Budget'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'proposal' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <FileText size={20} className="mr-2" />
                Proposal Generator
              </h3>
              <p className="text-white/60 text-sm">Generate professional proposals from RFPs</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Select RFP *</label>
                <select
                  value={selectedRfpId}
                  onChange={(e) => setSelectedRfpId(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                >
                  <option value="">Select an RFP...</option>
                  {rfps.map(rfp => (
                    <option key={rfp.rfp_id} value={rfp.rfp_id}>
                      {rfp.client_name} - {rfp.event_type}
                    </option>
                  ))}
                </select>
              </div>
              {selectedRfpId && rfps.find(r => r.rfp_id === selectedRfpId) && (
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-sm text-white/80">
                    <strong>Client:</strong> {rfps.find(r => r.rfp_id === selectedRfpId)?.client_name}
                  </p>
                  <p className="text-sm text-white/80">
                    <strong>Event:</strong> {rfps.find(r => r.rfp_id === selectedRfpId)?.event_type}
                  </p>
                  <p className="text-sm text-white/80">
                    <strong>Date:</strong> {rfps.find(r => r.rfp_id === selectedRfpId)?.event_date}
                  </p>
                </div>
              )}
              <Button
                onClick={handleGenerateProposal}
                disabled={loading || !selectedRfpId}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Generating...' : 'Generate Proposal'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* Result Display */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6 min-h-[400px]">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <Sparkles size={20} className="mr-2 text-[#D4AF37]" />
            AI Result
          </h3>
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : result ? (
            <div className="prose prose-invert max-w-none">
              <div className="text-xs text-white/40 mb-2">
                Generated at: {new Date(result.data.generated_at || result.data.analyzed_at).toLocaleString()}
              </div>
              <div className="whitespace-pre-wrap text-white/80 text-sm leading-relaxed overflow-y-auto max-h-[500px]">
                {result.data.concept || result.data.recommendations || result.data.optimized_flow || result.data.analysis || result.data.proposal}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-white/40">
              <Bot size={48} className="mb-4" />
              <p>Select a tool and generate content</p>
              <p className="text-sm mt-2">AI results will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enterprise Section Component
const EnterpriseSection = () => {
  const [legalContent, setLegalContent] = useState({});
  const [loadingType, setLoadingType] = useState(null);

  const loadLegalContent = async (type) => {
    setLoadingType(type);
    try {
      const response = await api.getLegalContent(type);
      setLegalContent(prev => ({ ...prev, [type]: response.data }));
    } catch (error) {
      console.error('Failed to load legal content:', error);
      toast.error('Failed to load legal content');
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Shield className="text-[#D4AF37] mr-3" size={28} />
        <div>
          <h2 className="text-2xl font-medium">Enterprise & Compliance</h2>
          <p className="text-white/60 text-sm">Manage legal pages, GDPR compliance, and audit logs</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Legal Pages */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Legal Documents</h3>
          <div className="space-y-3">
            <div 
              onClick={() => loadLegalContent('privacy')}
              className="flex items-center justify-between p-4 bg-white/5 rounded cursor-pointer hover:bg-white/10 transition-all"
            >
              <span>Privacy Policy</span>
              <FileText size={20} className="text-[#D4AF37]" />
            </div>
            <div 
              onClick={() => loadLegalContent('terms')}
              className="flex items-center justify-between p-4 bg-white/5 rounded cursor-pointer hover:bg-white/10 transition-all"
            >
              <span>Terms of Service</span>
              <FileText size={20} className="text-[#D4AF37]" />
            </div>
            <div 
              onClick={() => loadLegalContent('cookies')}
              className="flex items-center justify-between p-4 bg-white/5 rounded cursor-pointer hover:bg-white/10 transition-all"
            >
              <span>Cookie Policy</span>
              <FileText size={20} className="text-[#D4AF37]" />
            </div>
          </div>
        </div>

        {/* GDPR Compliance */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">GDPR Compliance</h3>
          <div className="space-y-4">
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded">
              <div className="flex items-center mb-2">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
                <span className="text-green-400 font-medium">Compliant</span>
              </div>
              <p className="text-white/60 text-sm">Data protection measures are in place</p>
            </div>
            <div className="space-y-2 text-sm text-white/60">
              <p>✓ User consent management</p>
              <p>✓ Data export capabilities</p>
              <p>✓ Right to erasure support</p>
              <p>✓ Data processing agreements</p>
            </div>
          </div>
        </div>

        {/* Audit Logs */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6 col-span-full">
          <h3 className="text-lg font-medium mb-4">Recent Audit Activity</h3>
          <p className="text-white/60 text-sm mb-4">Track user actions and system changes</p>
          <div className="space-y-2">
            {[
              { action: 'User Login', user: 'admin@nehpets.com', time: '2 min ago' },
              { action: 'Project Created', user: 'pm@nehpets.com', time: '15 min ago' },
              { action: 'Invoice Sent', user: 'admin@nehpets.com', time: '1 hour ago' },
              { action: 'RFP Status Updated', user: 'pm@nehpets.com', time: '2 hours ago' },
            ].map((log, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded text-sm">
                <span className="text-white/80">{log.action}</span>
                <span className="text-white/40">{log.user}</span>
                <span className="text-white/40">{log.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legal Content Modal */}
      {Object.keys(legalContent).length > 0 && (
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Legal Content Preview</h3>
          <div className="prose prose-invert max-w-none">
            {Object.entries(legalContent).map(([type, content]) => (
              <div key={type} className="mb-6">
                <h4 className="text-[#D4AF37] capitalize">{type}</h4>
                <div className="whitespace-pre-wrap text-white/70 text-sm">
                  {content.content || 'Loading...'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Financial Dashboard Component
const FinancialDashboard = ({ invoices }) => {
  const [data, setData] = useState(null);
  const [profitability, setProfitability] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      const [dashRes, profRes] = await Promise.all([
        api.getFinancialDashboard(),
        api.getProjectProfitability().catch(() => ({ data: [] }))
      ]);
      setData(dashRes.data);
      setProfitability(profRes.data);
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-medium mb-2">Financial Dashboard</h1>
        <p className="text-white/60">Revenue, invoices, and financial analytics</p>
      </div>

      {/* Financial Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <DollarSign className="text-[#D4AF37] mb-3" size={24} />
          <div className="text-3xl font-bold text-[#D4AF37] mb-2">
            ₦{(data?.total_revenue || 0).toLocaleString()}
          </div>
          <div className="text-white/60 text-sm">Total Revenue</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <TrendingUp className="text-yellow-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-yellow-400 mb-2">
            ₦{(data?.outstanding || 0).toLocaleString()}
          </div>
          <div className="text-white/60 text-sm">Outstanding</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <Users className="text-blue-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-blue-400 mb-2">
            {data?.active_subscriptions || 0}
          </div>
          <div className="text-white/60 text-sm">Active Subscriptions</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <FileText className="text-green-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-green-400 mb-2">
            {data?.paid_invoices || 0}
          </div>
          <div className="text-white/60 text-sm">Paid Invoices</div>
        </div>
      </div>

      {/* Revenue Trend */}
      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded-lg mb-8">
        <h3 className="text-lg font-medium mb-4">Monthly Revenue Trend</h3>
        <div className="h-48 flex items-end space-x-2">
          {(data?.revenue_by_month || []).slice(0, 12).reverse().map((item, i) => {
            const maxRevenue = Math.max(...(data?.revenue_by_month || []).map(r => r.revenue || 0), 1);
            const height = ((item.revenue || 0) / maxRevenue) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div className="text-xs text-white/40 mb-1">₦{((item.revenue || 0) / 1000000).toFixed(1)}M</div>
                <div
                  className="w-full bg-[#D4AF37] rounded-t transition-all hover:bg-[#F2D06B]"
                  style={{ height: `${height}%`, minHeight: '4px' }}
                />
                <span className="text-xs text-white/40 mt-2">{item.month?.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Project Profitability */}
      <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-medium mb-4">Project Profitability</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-white/10">
              <tr>
                <th className="text-left py-3 text-white/60 text-sm">Project</th>
                <th className="text-right py-3 text-white/60 text-sm">Revenue</th>
                <th className="text-right py-3 text-white/60 text-sm">Vendor Costs</th>
                <th className="text-right py-3 text-white/60 text-sm">Profit</th>
                <th className="text-right py-3 text-white/60 text-sm">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {profitability.slice(0, 5).map((p, i) => (
                <tr key={i} className="hover:bg-white/5">
                  <td className="py-3 font-medium">{p.project_name}</td>
                  <td className="py-3 text-right text-green-400">₦{(p.actual_revenue || 0).toLocaleString()}</td>
                  <td className="py-3 text-right text-red-400">₦{(p.vendor_costs || 0).toLocaleString()}</td>
                  <td className="py-3 text-right text-[#D4AF37] font-bold">₦{(p.profit || 0).toLocaleString()}</td>
                  <td className="py-3 text-right">
                    <span className={`px-2 py-1 rounded text-xs ${p.margin > 30 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {p.margin?.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">Recent Invoices</h3>
        <div className="space-y-3">
          {invoices.slice(0, 5).map((inv) => (
            <div key={inv.invoice_id} className="flex items-center justify-between p-3 bg-white/5 rounded">
              <div>
                <p className="font-medium">{inv.invoice_number}</p>
                <p className="text-xs text-white/40">{inv.client_name || 'Client'}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-[#D4AF37]">₦{inv.amount?.toLocaleString()}</p>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  inv.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                  inv.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {inv.status?.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Analytics Dashboard Component
const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    fetchAnalytics();
    
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.getAnalyticsDashboard(timeRange);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-medium mb-2">Analytics</h1>
          <p className="text-white/60">Comprehensive business insights and metrics</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="bg-[#0A0A0F] border border-white/10 rounded px-4 py-2 text-white"
        >
          <option value="week">Last 7 days</option>
          <option value="month">Last 30 days</option>
          <option value="quarter">Last 90 days</option>
          <option value="year">Last year</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <TrendingUp className="text-[#D4AF37] mb-3" size={24} />
          <div className="text-3xl font-bold text-[#D4AF37] mb-2">
            ₦{(data?.financial?.total_revenue || 0).toLocaleString()}
          </div>
          <div className="text-white/60 text-sm">Total Revenue</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <PieChart className="text-green-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-green-400 mb-2">
            {data?.financial?.profit_margin || 0}%
          </div>
          <div className="text-white/60 text-sm">Profit Margin</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <Calendar className="text-blue-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-blue-400 mb-2">
            {data?.projects?.active || 0}
          </div>
          <div className="text-white/60 text-sm">Active Projects</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <Users className="text-purple-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-purple-400 mb-2">
            {data?.users?.clients || 0}
          </div>
          <div className="text-white/60 text-sm">Total Clients</div>
        </div>
      </div>

      {/* Events by Stage */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">Events by Stage</h3>
          <div className="space-y-4">
            {Object.entries(data?.projects?.by_phase || {}).map(([phase, count]) => (
              <div key={phase} className="flex items-center">
                <span className="text-white/60 w-24 capitalize">{phase.replace('_', ' ')}</span>
                <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
                  <div 
                    className="bg-[#D4AF37] h-full"
                    style={{ width: `${(count / (data?.projects?.total || 1)) * 100}%` }}
                  />
                </div>
                <span className="text-white/80 ml-4 w-8 text-right">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">RFP Conversion</h3>
          <div className="text-center">
            <div className="text-6xl font-bold text-[#D4AF37] mb-2">
              {data?.rfps?.conversion_rate || 0}%
            </div>
            <p className="text-white/60">RFP to Project Conversion Rate</p>
            <div className="flex justify-center space-x-8 mt-6 text-sm">
              <div>
                <p className="text-2xl font-bold">{data?.rfps?.total || 0}</p>
                <p className="text-white/60">Total RFPs</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{data?.rfps?.by_status?.converted_to_project || 0}</p>
                <p className="text-white/60">Converted</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Workload */}
      <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-medium mb-4">Team Workload</h3>
        <div className="space-y-4">
          {(data?.team_workload || []).map((member, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center text-[#D4AF37] font-bold mr-3">
                  {member.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium">{member.name}</p>
                  <p className="text-xs text-white/40">{member.email}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#D4AF37]">{member.project_count}</p>
                <p className="text-xs text-white/40">Active Projects</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Tool Usage */}
      <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4">AI Tool Usage</h3>
        {Object.keys(data?.ai_usage || {}).length === 0 ? (
          <p className="text-white/40">No AI usage data available</p>
        ) : (
          <div className="grid grid-cols-5 gap-4">
            {Object.entries(data?.ai_usage || {}).map(([tool, count]) => (
              <div key={tool} className="bg-white/5 p-4 rounded text-center">
                <Sparkles className="mx-auto mb-2 text-[#D4AF37]" size={24} />
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs text-white/40 capitalize">{tool.replace('_', ' ')}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;